import { ButtonLink } from "design-system/atoms/Button"
import { Progress } from "design-system/atoms/Progress"
import { Loading } from "modules/shared/components/Loading"
import { useSubscriptionData } from "modules/subscription/hooks/useSubscriptionData"
import { useTranslation } from "react-i18next"
import { AutoTopup } from "./AutoTopup"

export function SidebarUsageContent() {
  const { t, i18n } = useTranslation()
  // NOTE: It's ok to have the hook here again, as when it re-renders, it will re-fetch the data
  const {
    isPending,
    totalSubscriptionCredits,
    usedSubscriptionCredits,
    subscriptionIsError,
    hasAddon,
    totalAddonCredits,
    usedAddonCredits,
    addonIsError,
    hasSubscription,
    subscriptionRenewalDate,
    activeSubscriptionName,
    autoTopUpPriceId,
  } = useSubscriptionData()

  if (isPending) {
    return <Loading />
  }

  return (
    <div className="flex flex-col gap-4 p-2 items-start w-75">
      <div className="flex flex-row gap-2 items-start w-full">
        <div className="flex flex-col gap-0 items-start w-full">
          <span className="text-md font-medium">{t("subscription.usage.title")}</span>
          {hasSubscription && subscriptionRenewalDate ? (
            <span className="text-base text-muted-foreground">
              {t("subscription.usage.renewalDate", {
                date: new Date(subscriptionRenewalDate).toLocaleDateString(i18n.language, {
                  month: "long",
                  day: "numeric",
                }),
              })}
            </span>
          ) : null}
          {!hasSubscription && (
            <span className="text-base text-warning">{t("subscription.usage.noSubscription")}</span>
          )}
        </div>
        <ButtonLink variant="outline" to="/settings/subscription/plans" className="shrink-0">
          {t("subscription.plans.cta.viewPlans")}
        </ButtonLink>
      </div>
      <div className="flex flex-col gap-2 items-start w-full">
        <span className="text-base font-medium">
          {activeSubscriptionName.includes("custom")
            ? t(`subscription.plans.definitions.custom.name`)
            : t(`subscription.plans.definitions.${activeSubscriptionName.split("_").at(-1)}.name`)}
        </span>
        <div className="flex flex-col gap-1 items-start w-full">
          <Progress
            value={usedSubscriptionCredits}
            max={totalSubscriptionCredits}
            color={subscriptionIsError ? "destructive" : "primary"}
          />
          <div className="flex flex-row justify-between w-full text-xs items-center font-mono">
            <span className="text-foreground">{usedSubscriptionCredits}</span>
            <span className="text-muted-foreground">{totalSubscriptionCredits}</span>
          </div>
        </div>
        {hasSubscription && (
          <div className="flex flex-col gap-1 items-start w-full text-muted-foreground text-sm">
            <span>{t("subscription.usage.planExplanation")}</span>
          </div>
        )}
      </div>
      {!hasSubscription && (
        <div className="flex flex-col gap-1 items-start w-full text-muted-foreground text-sm">
          <span className="font-medium">{t("subscription.usage.joinAPlan.title")}</span>
          <span>{t("subscription.usage.joinAPlan.description")}</span>
        </div>
      )}
      {hasAddon && (
        <div className="flex flex-col gap-2 items-start w-full">
          <span className="text-base font-medium">{t("subscription.usage.addon")}</span>
          <div className="flex flex-col gap-1 items-start w-full">
            <Progress
              value={usedAddonCredits}
              max={totalAddonCredits}
              color={addonIsError ? "destructive" : "gradient"}
            />
            <div className="flex flex-row justify-between w-full text-xs items-center font-mono">
              <span className="text-foreground">{usedAddonCredits}</span>
              <span className="text-muted-foreground">{totalAddonCredits}</span>
            </div>
          </div>
          <div className="flex flex-col gap-1 items-start w-full text-muted-foreground text-sm pt-4">
            <span className="font-medium">{t("subscription.usage.addonExplanation.first")}</span>
            <span>{t("subscription.usage.addonExplanation.second")}</span>
          </div>
        </div>
      )}
      <AutoTopup
        autoTopupEnabled={!!autoTopUpPriceId}
        hasSubscription={hasSubscription}
        addonIsError={addonIsError}
      />
    </div>
  )
}
